/* Bus writes for the console (2026-10-28), moved out of the classic
   dashboard. Each one is the classic write or the server's own shape:

   - addBus: the classic dashboard never had an add-bus form (only Zana's
     create_bus and a driver's sign-up made buses). This writes the record the
     server's buildBusDoc writes (driverSignup.js), owned by the admin who
     adds it — `ownerId == uid` is what the rules let a client create.
   - removeBus: classic handleDeleteBus — a soft delete. Drivers come off the
     bus and are signed out, parents' children lose the bus, the bus goes
     inactive; its trips and fuel stay in the reports.
   - saveBusDetails: classic buildFuelBenchmarkUpdates — the fields the rules
     let any fleet admin of the bus's org write. */
import {
  arrayRemove, arrayUnion, doc, FieldValue, getDoc, serverTimestamp, updateDoc, writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase';
import type { Bus, FleetModel } from './model';

/** "kda 442-k" → "KDA442K" — the document id and numberPlate the server uses. */
export const plateKey = (raw: string) => String(raw || '').toUpperCase().replace(/[\s-]/g, '');

/** "KDA442K" → "KDA 442K" for people. */
export const platePretty = (key: string) => key.replace(/^([A-Z]{3})(\d{3}[A-Z]?)$/, '$1 $2');

/** Kenyan plates: 3 letters, 3 numbers, a letter (older ones end on the numbers). */
export const PLATE_RE = /^[A-Z]{3}\d{3}[A-Z]?$/;
export const PLATE_HINT = 'Plate looks like KDA 442K — 3 letters, 3 numbers, 1 letter';

export type AddBusResult =
  | { ok: true; plate: string; revived?: boolean }
  | { ok: false; field?: 'plate'; message: string };

const orgFields = (code: string) => ({ schoolCode: code, organizationCode: code, orgCode: code });

/** A driver moves to this bus: off the bus they were on, onto this one. */
function assignDriver(batch: ReturnType<typeof writeBatch>, driverId: string, fromBusId: string | null, toPlate: string, toName: string) {
  if (fromBusId && fromBusId !== toPlate) {
    batch.update(doc(db, 'buses', fromBusId), { driverIds: arrayRemove(driverId), updatedAt: serverTimestamp() });
  }
  batch.update(doc(db, 'users', driverId), { busId: toPlate, busNickname: toName, updatedAt: serverTimestamp() });
}

export async function addBus(
  f: FleetModel,
  uid: string,
  input: { plate: string; seats: string; makeModel: string; driverId: string | null; nickname?: string },
): Promise<AddBusResult> {
  const plate = plateKey(input.plate);
  if (!PLATE_RE.test(plate)) return { ok: false, field: 'plate', message: PLATE_HINT };
  const code = f.org.code;
  if (!code) return { ok: false, message: 'Your school is still loading. Try again in a moment.' };

  const seats = parseInt(input.seats, 10);
  const mm = input.makeModel.trim().replace(/\s+/g, ' ');
  const [make, ...rest] = mm ? mm.split(' ') : [];
  const pretty = platePretty(plate);
  const name = input.nickname?.trim().slice(0, 40) || pretty;
  const driver = input.driverId ? f.drivers.find((d) => d.id === input.driverId) || null : null;

  const ref = doc(db, 'buses', plate);
  let existing: Record<string, unknown> | null = null;
  try {
    const snap = await getDoc(ref);
    existing = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
  } catch {
    // The rules let anyone see that a plate is free, and only its own school
    // read a bus that exists — so a refused read is another school's bus.
    return { ok: false, field: 'plate', message: `${pretty} is already on Trasse with another school. Call us if it is yours.` };
  }

  if (existing) {
    const mine = [existing.schoolCode, existing.organizationCode, existing.orgCode].includes(code);
    if (!mine) return { ok: false, field: 'plate', message: `${pretty} is already on Trasse with another school. Call us if it is yours.` };
    if (existing.isActive !== false) return { ok: false, field: 'plate', message: `${pretty} is already in your fleet.` };
    // Removed earlier: bring it back (status + isActive are an admin's to set).
    // Two saves, not one: the rules allow status and drivers as separate
    // field lists, never together in one write.
    await updateDoc(ref, { isActive: true, status: 'active', updatedAt: serverTimestamp() });
    if (driver) {
      const batch = writeBatch(db);
      batch.update(ref, { driverIds: arrayUnion(driver.id), updatedAt: serverTimestamp() });
      assignDriver(batch, driver.id, driver.busId, plate, str(existing.nickname) || name);
      await batch.commit();
    }
    return { ok: true, plate, revived: true };
  }

  const batch = writeBatch(db);
  batch.set(ref, {
    ownerId: uid,
    driverIds: driver ? [driver.id] : [],
    nickname: name,
    numberPlate: plate,
    ...orgFields(code),
    children: [],
    currentRoute: { type: null, startedAt: null, finishedAt: null, status: null },
    isActive: true,
    status: 'active',
    fuelType: 'diesel',
    ...(Number.isFinite(seats) && seats > 0 ? { seatCapacity: seats } : {}),
    ...(make ? { vehicleMake: make } : {}),
    ...(rest.length ? { vehicleModel: rest.join(' ') } : {}),
    source: 'console',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  if (driver) assignDriver(batch, driver.id, driver.busId, plate, name);
  await batch.commit();
  return { ok: true, plate };
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** Roster children with the parent that owns them. */
export function rosterOf(bus: Bus): { childId: string; name: string; parentId: string }[] {
  const raw = Array.isArray(bus.raw.children) ? (bus.raw.children as Record<string, unknown>[]) : [];
  return raw
    .filter((c) => c && typeof c === 'object')
    .map((c) => ({ childId: str(c.childId) || str(c.id), name: str(c.childName) || str(c.name), parentId: str(c.parentId) }));
}

export const driverIdsOf = (bus: Bus): string[] =>
  (Array.isArray(bus.raw.driverIds) ? (bus.raw.driverIds as unknown[]) : []).map(String).filter(Boolean);

/** Classic handleDeleteBus, field for field. */
export async function removeBus(bus: Bus): Promise<void> {
  const batch = writeBatch(db);
  for (const driverId of driverIdsOf(bus)) {
    batch.update(doc(db, 'users', driverId), {
      busId: null,
      isBusOwner: null,
      busNickname: null,
      isLoggedOut: true,
      logoutReason: 'bus_deleted',
      logoutTimestamp: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
  const parentIds = [...new Set(rosterOf(bus).map((c) => c.parentId).filter(Boolean))];
  for (const parentId of parentIds) {
    const parentRef = doc(db, 'users', parentId);
    const parentSnap = await getDoc(parentRef);
    if (!parentSnap.exists()) continue;
    const data = parentSnap.data();
    const children = ((data.children as Record<string, unknown>[]) || []).map((c) =>
      (c as { busId?: string }).busId === bus.id ? { ...c, busId: null } : c);
    const busChildren = { ...((data.busChildren as Record<string, string>) || {}) };
    Object.keys(busChildren).forEach((k) => { if (busChildren[k] === bus.id) delete busChildren[k]; });
    batch.update(parentRef, { children, busChildren, updatedAt: serverTimestamp() });
  }
  await batch.commit();
  await updateDoc(doc(db, 'buses', bus.id), { isActive: false, updatedAt: serverTimestamp() });
}

export interface BusDetailsInput { tank: string; benchmark: string; year: string; unit: 'km' | 'miles'; odometer: string }

export function busDetailsOf(bus: Bus): BusDetailsInput {
  const r = bus.raw;
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? String(v) : '');
  return {
    tank: n(r.fuelTankCapacityL ?? r.fuelCapacityLiters),
    benchmark: n(r.benchmarkKmPerL ?? r.benchmarkKmPerLiter),
    year: n(r.yearOfManufacture),
    unit: r.odometerUnit === 'miles' || r.odometerUnit === 'mi' ? 'miles' : 'km',
    odometer: typeof r.odometerKm === 'number' && r.odometerKm > 0 ? String(Math.round(r.odometerKm)) : '',
  };
}

/** What is wrong with the details, per field — null when they can be saved. */
export function busDetailsErrors(d: BusDetailsInput): Partial<Record<keyof BusDetailsInput, string>> {
  const e: Partial<Record<keyof BusDetailsInput, string>> = {};
  const now = new Date().getFullYear();
  if (d.tank.trim()) {
    const v = parseFloat(d.tank);
    if (!(v >= 50 && v <= 500)) e.tank = 'A bus tank holds 50 to 500 litres.';
  }
  if (d.benchmark.trim() && !(parseFloat(d.benchmark) > 0)) e.benchmark = 'Enter the kilometres it does on a litre, like 6.5.';
  if (d.year.trim()) {
    const y = parseInt(d.year, 10);
    if (!(y >= 1980 && y <= now)) e.year = `A year from 1980 to ${now}.`;
  }
  if (d.odometer.trim()) {
    const v = Number(d.odometer);
    if (!(v > 0 && v <= 3_000_000)) e.odometer = 'Type the number on the dash, like 118400.';
  }
  return e;
}

/** Classic buildFuelBenchmarkUpdates: both spellings, blanks clear. The odometer
    goes only when it was changed (`before` is what the dialog opened with): the
    phone writes it at each refill and trip start, and a dialog left open must
    not put an older reading back. The rules let a fleet admin write it with
    these fields (fleetAdminBusKeysOk). */
export async function saveBusDetails(busId: string, d: BusDetailsInput, before?: BusDetailsInput): Promise<void> {
  const u: { [k: string]: FieldValue | number | string | null } = { updatedAt: serverTimestamp() };
  const cap = parseFloat(d.tank);
  if (d.tank.trim() && cap >= 50 && cap <= 500) { u.fuelTankCapacityL = cap; u.fuelCapacityLiters = cap; }
  const bench = parseFloat(d.benchmark);
  if (d.benchmark.trim() && bench > 0) { u.benchmarkKmPerL = bench; u.benchmarkKmPerLiter = bench; } else if (!d.benchmark.trim()) { u.benchmarkKmPerL = null; u.benchmarkKmPerLiter = null; }
  const yr = parseInt(d.year, 10);
  if (d.year.trim() && yr >= 1980 && yr <= new Date().getFullYear()) u.yearOfManufacture = yr; else if (!d.year.trim()) u.yearOfManufacture = null;
  u.odometerUnit = d.unit;
  const odo = Number(d.odometer);
  if (d.odometer.trim() && odo > 0 && odo <= 3_000_000 && d.odometer.trim() !== (before?.odometer ?? '')) u.odometerKm = Math.round(odo);
  await updateDoc(doc(db, 'buses', busId), u);
}
